import { dayLabel, fromIsoDay, hourLabel, isoDay, monthTitle, shortDayLabel } from 'helper/timeLabels';

/**
 * The activity of a record in one shape, whatever it comes from: the calls,
 * e-mails, appointments, tasks and notes of the sales team, and what the
 * client did in Jobbiz. Pure: the texts come from `text` (recordActivity.*).
 */

export const ACTIVITY_FILTERS = ['all', 'email', 'call', 'task', 'note', 'client'];

// What the client does in Jobbiz, as the api traces it. `minor`: too frequent
// for « Tout », kept for « Actions clients »; `tab`: the tab of the company that shows it.
export const CLIENT_EVENTS = {
  mission_created: { tab: 'missions' },
  mission_canceled: { tab: 'missions' },
  contract_signed: { tab: 'missions', minor: true },
  timesheet_validated: { tab: 'missions', minor: true },
  worker_review: { tab: 'missions' },
  member_added: { tab: 'contacts' },
};

/**
 * Every line of a record's activity. `record`: { calls, emails, events,
 * tasks, notes, clientEvents, demoRequests }; `nameOf(id)`: a colleague's name; `contact`:
 * on a contact's page, the name that need not be repeated.
 */
// A note of the api says who may edit it or delete it (`canEdit`,
// `canDelete`); the activity offers those gestures only.
const noteLine = (note, { text, nameOf }) => ({
  key: `n${note.id}`,
  kind: 'note',
  at: new Date(note.at ?? note.createdAt),
  title: text('noteBy', { name: nameOf(note.authorId) }),
  body: note.body,
  note:
    note.canEdit || note.canDelete
      ? { id: note.id, body: note.body, links: note.links ?? [], canEdit: Boolean(note.canEdit), canDelete: Boolean(note.canDelete) }
      : null,
});

// « 3:07 »: a call's talk time, in minutes and seconds.
export const callDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const PHONE_DIRECTIONS = ['inbound', 'outbound', 'internal'];

/**
 * A Wildix call (record/calls): its direction, the person of Jobbiz on it
 * (`nameOf(userId, '-')`), the contact or else the number, its talk time or
 * « Manqué ». A click writes a note on it (`ref.kind: 'phoneCall'`).
 */
const phoneCallLine = (call, { text, nameOf, contact, notes }) => {
  const other = call.contactName ? (call.contactName === contact ? '' : call.contactName) : call.remotePhone ?? '';
  const direction = PHONE_DIRECTIONS.includes(call.direction) ? call.direction : 'unknown';
  return {
    key: `p${call.id}`,
    kind: 'call',
    at: new Date(call.at),
    title: [text(`phoneCallDirection.${direction}`), call.userId ? nameOf(call.userId, '-') : '-', other].filter(Boolean).join(' · '),
    callSource: text('callSource.wildix'),
    missed: Boolean(call.missed),
    duration: call.missed ? undefined : text('phoneCallDuration', { duration: callDuration(call.talkSeconds) }),
    ref: call.noteLink ? { kind: 'phoneCall', id: call.id, link: call.noteLink } : undefined,
    notes,
  };
};

// An open task is late once its time is past, or, without a time, once its day is.
const taskIsLate = (task, now) =>
  task.dueTime ? new Date(`${task.dueOn}T${task.dueTime.slice(0, 5)}`) < now : task.dueOn < isoDay(now);

export const buildActivity = (record, { now, text, nameOf, contact = null }) => {
  const out = [];
  // A note written on a call shows under it, and not again among the notes:
  // `callId` a call logged in the CRM, `phoneCallId` a Wildix call.
  const calls = record.calls ?? [];
  const callIds = new Set(calls.filter((call) => !call.wildix).map((call) => call.id));
  const phoneCallIds = new Set(calls.filter((call) => call.wildix).map((call) => call.id));
  const callNotes = (record.notes ?? []).filter(
    (note) => (note.callId && callIds.has(note.callId)) || (note.phoneCallId && phoneCallIds.has(note.phoneCallId)),
  );
  const notesOn = (field, id) =>
    callNotes
      .filter((note) => note[field] === id)
      .map((note) => noteLine(note, { text, nameOf }))
      .sort((a, b) => a.at - b.at);
  // Each call says where it comes from: Wildix, or logged by hand (prospection
  // included). A crmCall is never merged into a Wildix call: its phoneCallId
  // points to a stored call document (ObjectId), not to the Wildix id shown.
  calls.forEach((call) => {
    if (call.wildix) {
      out.push(phoneCallLine(call, { text, nameOf, contact, notes: notesOn('phoneCallId', call.id) }));
      return;
    }
    const at = new Date(call.at);
    const who = call.contactName && call.contactName !== contact ? ` · ${call.contactName}` : '';
    out.push({
      key: `c${call.id}`,
      kind: 'call',
      at,
      title: `${text('callBy', { name: nameOf(call.authorId) })}${who}`,
      callSource: text('callSource.manual'),
      body: call.note || text(`callResult.${call.result}`),
      // A call of the api takes a note: `noteLink`, the record it goes to.
      ref: call.noteLink ? { kind: 'call', id: call.id, link: call.noteLink } : undefined,
      notes: notesOn('callId', call.id),
    });
  });
  (record.emails ?? []).forEach((email) => {
    const sent = email.direction === 'sent';
    // The api's status, not the time, says whether a sent e-mail is still to
    // leave: its dispatch may run late. Absent: an e-mail sent before scheduling existed.
    const status = sent ? (email.status ?? 'sent') : null;
    if (status === 'canceled') return;
    const future = status === 'scheduled';
    const failed = status === 'failed';
    const at = new Date(
      (future || failed) && email.scheduledAt ? email.scheduledAt : email.at,
    );
    const address = sent && email.to?.length ? email.to.join(', ') : email.address;
    const titleKey = future
      ? 'emailScheduled'
      : failed
        ? 'emailFailed'
        : sent
          ? 'emailTo'
          : 'emailFrom';
    out.push({
      key: `m${email.id}`,
      kind: 'email',
      at,
      future,
      failed,
      // Only its author may cancel it: ActivityEntry compares with the signed-in user.
      scheduledEmail: future
        ? { id: email.id, authorId: email.authorId }
        : undefined,
      when: future ? `${dayLabel(at)} · ${hourLabel(at)}` : undefined,
      title: text(titleKey, { address }),
      subject: email.subject,
      body: email.body,
      // `senderEmail`: the address it left from; null on the e-mails sent before it was chosen.
      header: sent
        ? text('emailHeaderSent', {
            from: email.senderEmail ? `${nameOf(email.authorId)} <${email.senderEmail}>` : nameOf(email.authorId),
            to: email.contactName,
            address: email.address,
          })
        : text('emailHeaderReceived', { from: email.contactName || email.address }),
      // The api does not track openings: without `opens`, nothing is said of them.
      opens:
        sent && !future && !failed && email.opens
          ? email.opens.map((day) => new Date(day))
          : null,
    });
  });
  (record.events ?? []).forEach((event) => {
    const start = new Date(event.startsAt);
    const future = start > now;
    const mode = event.mode ? ` · ${event.mode}` : '';
    const withWhom = event.contactName && event.contactName !== contact
      ? text('meetingWith', { name: event.contactName })
      : text('meeting');
    out.push({
      key: `e${event.id}`,
      kind: 'event',
      at: start,
      future,
      title: future ? event.title || withWhom : withWhom,
      when: `${future ? dayLabel(start) : shortDayLabel(isoDay(start))} · ${hourLabel(start)}${mode}`,
      body: event.outcome || event.description,
    });
  });
  (record.tasks ?? []).forEach((task) => {
    const done = Boolean(task.doneAt);
    out.push({
      key: `t${task.id}`,
      kind: 'task',
      at: done ? new Date(task.doneAt) : fromIsoDay(task.dueOn),
      future: !done,
      late: !done && taskIsLate(task, now),
      title: text(done ? 'taskDoneBy' : 'taskFor', { name: nameOf(task.assigneeId) }),
      when: done
        ? shortDayLabel(isoDay(new Date(task.doneAt)))
        : text('taskDue', {
            // Its time too when it has one, as an appointment's: « ven. 30 oct. · 14 h 00 ».
            day: [
              dayLabel(fromIsoDay(task.dueOn)).toLowerCase(),
              task.dueTime && hourLabel(new Date(`${task.dueOn}T${task.dueTime.slice(0, 5)}`)),
            ]
              .filter(Boolean)
              .join(' · '),
          }),
      body: task.title,
      ref: { kind: 'task', id: task.id },
    });
  });
  // A call to make from the call list, still open: to come, late once due.
  (record.callbacks ?? []).forEach((callback) => {
    const at = new Date(callback.dueAt);
    out.push({
      key: `cb${callback.id}`,
      kind: 'callback',
      at,
      future: true,
      late: at < now,
      title: `Rappel pour ${nameOf(callback.ownerId)}`,
      when: `${dayLabel(at)} · ${hourLabel(at)}`,
      body: callback.kind === 'new' ? 'Premier appel · liste d’appel' : 'À rappeler · liste d’appel',
      ref: { kind: 'callback', id: callback.id },
    });
  });
  (record.notes ?? []).forEach((note) => {
    if (!callNotes.includes(note)) out.push(noteLine(note, { text, nameOf }));
  });
  (record.clientEvents ?? []).forEach((event) => {
    const def = CLIENT_EVENTS[event.kind];
    if (!def) return;
    // A review says whether it is good or bad in its own words.
    const key =
      event.kind === 'worker_review'
        ? `client.worker_review_${event.details?.feedback === 'negative' ? 'negative' : 'positive'}`
        : `client.${event.kind}`;
    const action = text(key, event.details ?? {});
    out.push({
      key: `x${event.id}`,
      kind: 'client',
      eventKind: event.kind,
      details: event.details ?? {},
      at: new Date(event.at),
      minor: Boolean(def.minor),
      self: Boolean(contact) && event.actor === contact,
      actor: event.actor,
      action,
      ref: { kind: 'client', tab: def.tab, companyId: event.companyId, missionId: event.missionId },
    });
  });
  // A demo request of the site (JBWEB-1420), on the day it came in.
  (record.demoRequests ?? []).forEach((request) => {
    const at = new Date(request.at);
    out.push({
      key: `d${request.id}`,
      kind: 'demoRequest',
      at,
      title: text('demoRequestOn', { day: shortDayLabel(isoDay(at)) }),
      // An old request has no department (''): nothing under its title.
      ...(request.department ? { body: text('demoRequestDepartment', { department: request.department }) } : {}),
    });
  });
  return out;
};

// The activity of several records at once (a group and its companies): each
// line says which one it comes from.
export const sourcesActivity = (sources, options) =>
  sources.flatMap((source) =>
    buildActivity(source.record, options).map((item) => ({
      ...item,
      key: `${source.id}-${item.key}`,
      source: source.id,
      from: source.label,
      ref: item.ref && { ...item.ref, companyId: item.ref.companyId ?? source.companyId },
    })),
  );

// An appointment is a task: « Tâches » shows both. « Tout » leaves out the
// client's minor actions.
export const inFilter = (item, filter) => {
  if (filter === 'all') return item.kind !== 'client' || !item.minor;
  return item.kind === filter || (filter === 'task' && ['event', 'callback'].includes(item.kind));
};

// The client's actions of a kind, on a day and in a record, in one line that
// unfolds; alone, an action keeps its line.
export const bundleClient = (items) => {
  const out = [];
  const open = new Map();
  items.forEach((item) => {
    if (item.kind !== 'client') {
      out.push(item);
      return;
    }
    const key = `${item.source ?? ''}|${item.eventKind}|${isoDay(item.at)}`;
    if (open.has(key)) open.get(key).bundle.push(item);
    else {
      const bundle = { ...item, key: `b-${item.key}`, bundle: [item] };
      open.set(key, bundle);
      out.push(bundle);
    }
  });
  return out.map((item) => (item.bundle?.length === 1 ? item.bundle[0] : item));
};

/**
 * What a filter shows: what is to come (the soonest first), then the past by
 * month (the latest first), the client's actions of a day bundled.
 */
export const activitySections = (items, filter) => {
  const shown = items.filter((item) => inFilter(item, filter));
  const upcoming = shown.filter((item) => item.future).sort((a, b) => a.at - b.at);
  const past = bundleClient(shown.filter((item) => !item.future).sort((a, b) => b.at - a.at));
  const months = [];
  past.forEach((item) => {
    const label = monthTitle(item.at);
    if (!months.length || months[months.length - 1].label !== label) months.push({ label, items: [] });
    months[months.length - 1].items.push(item);
  });
  return { count: shown.length, upcoming, months };
};

export const countByFilter = (items) =>
  Object.fromEntries(ACTIVITY_FILTERS.map((filter) => [filter, items.filter((item) => inFilter(item, filter)).length]));
